'use client'

import React, { useId, useState } from 'react'
import { cn } from '@/utilities/ui'
import { PRIMARY_PALETTE, type PaletteColour } from './constants'
import { useInView } from './Reveal'
import { useCopyToClipboard } from './useCopyToClipboard'

/** Aurora is the signature colour, so the accordion opens on it by default. */
const DEFAULT_ACTIVE = PRIMARY_PALETTE.find((c) => c.name === 'Aurora')?.name ?? PRIMARY_PALETTE[0].name

const PaletteCard: React.FC<{ colour: PaletteColour; index: number }> = ({ colour, index }) => {
  const { ref, inView } = useInView<HTMLButtonElement>(0.25)
  const { copied, copy } = useCopyToClipboard()
  const dark = colour.tone === 'dark'

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => copy(colour.hex)}
      aria-label={`Copy ${colour.name} hex value ${colour.hex}`}
      style={{ backgroundColor: colour.bg, transitionDelay: inView ? `${index * 80}ms` : '0ms' }}
      className={cn(
        'group flex h-[320px] w-full flex-col rounded-[16px] p-6 text-left sm:h-[360px] sm:p-7',
        'transition-[opacity,transform,box-shadow] duration-500 ease-out',
        'hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(0,0,0,0.45)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black/40',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        colour.bordered && 'border border-civo-cloud',
        inView ? 'opacity-100' : 'translate-y-3 opacity-0',
        dark ? 'text-civo-white' : 'text-civo-black',
      )}
    >
      <div className="flex flex-col gap-2">
        {/* Split-flap hex: flips in on scroll, and replays on hover (group-hover:animate-flip) */}
        <span className="subheading-large block" style={{ perspective: '500px' }}>
          {colour.hex.split('').map((ch, i) => (
            <span
              key={i}
              style={{
                transitionDelay: inView ? `${i * 40}ms` : '0ms',
                animationDelay: `${i * 40}ms`,
              }}
              className={cn(
                'inline-block origin-bottom transition-[transform,opacity] duration-500',
                'group-hover:animate-flip motion-reduce:transition-none motion-reduce:opacity-100',
                'motion-reduce:[transform:none] motion-reduce:group-hover:animate-none',
                inView
                  ? 'opacity-100 [transform:rotateX(0deg)]'
                  : 'opacity-0 [transform:rotateX(-90deg)]',
              )}
            >
              {ch}
            </span>
          ))}
        </span>
        <span className={cn('body-small', dark ? 'text-white/70' : 'text-black/60')}>
          {colour.rgba}
        </span>
        <span className={cn('body-small', dark ? 'text-white/70' : 'text-black/60')}>
          {colour.cmyk}
        </span>
      </div>

      <div className="mt-auto flex items-end justify-between gap-2">
        <span className="subheading-large">{colour.name}</span>
        <span
          aria-live="polite"
          className={cn('body-small', dark ? 'text-white/70' : 'text-black/50')}
        >
          {copied ? 'Copied!' : 'Click to copy'}
        </span>
      </div>
    </button>
  )
}

/**
 * Tablet + mobile: an accordion of colour bands. One band is expanded showing the full
 * details; the rest collapse. Tablet expands horizontally (foot labels), mobile vertically.
 *
 * Each band is a full-area expand button (aria-expanded + aria-controls) with the content
 * laid over it as pointer-events-none, so a tap/click anywhere expands the band. The hex is
 * a separate copy button (only when expanded) - copy is never a side effect of expanding.
 */
const PaletteAccordion: React.FC = () => {
  const baseId = useId()
  const detailId = (name: string, bp: string) => `${baseId}-${bp}-${name.toLowerCase()}`
  const [active, setActive] = useState(DEFAULT_ACTIVE)
  const { copied, copy } = useCopyToClipboard()

  const expandTrigger = (colour: PaletteColour, isActive: boolean, controls: string) => (
    <button
      type="button"
      onClick={() => setActive(colour.name)}
      aria-expanded={isActive}
      aria-controls={controls}
      className="absolute inset-0 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-civo-black/40"
    >
      <span className="sr-only">
        {colour.name}
        {isActive ? ', selected' : ', show values'}
      </span>
    </button>
  )

  const hex = (colour: PaletteColour, isActive: boolean) =>
    isActive ? (
      <button
        type="button"
        onClick={() => copy(colour.hex)}
        aria-label={`Copy hex ${colour.hex}`}
        className="subheading-small pointer-events-auto rounded-[4px] opacity-90 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current motion-reduce:transition-none"
      >
        {colour.hex}
      </button>
    ) : (
      <span className="subheading-small opacity-90">{colour.hex}</span>
    )

  return (
    <>
      {/* Tablet: horizontal accordion - collapsed strips carry an upright foot label */}
      <div className="hidden h-[289px] w-full overflow-hidden rounded-[16px] border border-civo-cloud md:flex lg:hidden">
        {PRIMARY_PALETTE.map((colour) => {
          const isActive = colour.name === active
          const dark = colour.tone === 'dark'
          const did = detailId(colour.name, 't')
          return (
            <div
              key={colour.name}
              style={{ backgroundColor: colour.bg, flexGrow: isActive ? 6 : 1 }}
              className={cn(
                'relative flex h-full min-w-20 basis-0 overflow-hidden transition-[flex-grow] duration-500 ease-out motion-reduce:transition-none',
                dark ? 'text-civo-white' : 'text-civo-black',
              )}
            >
              {expandTrigger(colour, isActive, did)}
              {/* Collapsed: hex top, name upright at the foot */}
              <span
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-0 z-20 flex flex-col justify-between p-4 transition-opacity duration-200 ease-out motion-reduce:transition-none',
                  isActive ? 'opacity-0' : 'opacity-100',
                )}
              >
                <span className="body-xsmall opacity-90">{colour.hex}</span>
                <span className="subheading-xsmall whitespace-nowrap">{colour.name}</span>
              </span>
              {/* Expanded: name + hex header, RGBA/CMYK at the foot */}
              <span
                id={did}
                aria-hidden={!isActive}
                className={cn(
                  'pointer-events-none absolute inset-0 z-20 flex flex-col justify-between p-6 transition-opacity duration-300 ease-out motion-reduce:transition-none',
                  isActive ? 'opacity-100 delay-100' : 'opacity-0',
                )}
              >
                <span className="flex items-center justify-between gap-4 whitespace-nowrap">
                  <span className="subheading-large">{colour.name}</span>
                  {hex(colour, isActive)}
                </span>
                <span className="flex flex-col gap-1 whitespace-nowrap">
                  <span className="body-small opacity-75">{colour.rgba}</span>
                  <span className="body-small opacity-75">{colour.cmyk}</span>
                </span>
              </span>
            </div>
          )
        })}
      </div>

      {/* Mobile: vertical accordion - header always visible, details reveal on select */}
      <div className="flex flex-col overflow-hidden rounded-[16px] border border-civo-cloud md:hidden">
        {PRIMARY_PALETTE.map((colour) => {
          const isActive = colour.name === active
          const dark = colour.tone === 'dark'
          const did = detailId(colour.name, 'm')
          return (
            <div
              key={colour.name}
              style={{ backgroundColor: colour.bg }}
              className={cn('relative flex flex-col', dark ? 'text-civo-white' : 'text-civo-black')}
            >
              {expandTrigger(colour, isActive, did)}
              <div className="pointer-events-none relative z-20 flex flex-col p-6">
                <span className="flex items-center justify-between gap-4">
                  <span className="subheading-large">{colour.name}</span>
                  {hex(colour, isActive)}
                </span>
                <span
                  id={did}
                  aria-hidden={!isActive}
                  className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                    isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <span className="overflow-hidden">
                    <span className="mt-3 flex flex-col gap-1">
                      <span className="body-small opacity-75">{colour.rgba}</span>
                      <span className="body-small opacity-75">{colour.cmyk}</span>
                    </span>
                  </span>
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <span aria-live="polite" className="sr-only">
        {copied ? 'Copied!' : ''}
      </span>
    </>
  )
}

export const PrimaryPalette: React.FC = () => (
  <>
    {/* Desktop: 4-up card grid */}
    <div className="hidden gap-4 sm:gap-5 lg:grid lg:grid-cols-4">
      {PRIMARY_PALETTE.map((colour, i) => (
        <PaletteCard key={colour.name} colour={colour} index={i} />
      ))}
    </div>
    {/* Tablet & mobile: accordion */}
    <PaletteAccordion />
  </>
)
